import { useState, useCallback } from 'react';
import { useAuthenticatedApi } from '../../../../../hooks/useAuthenticatedApi';

const useDatabase = (workspaceId) => {
  const authenticatedApi = useAuthenticatedApi();
  
  const [collections, setCollections] = useState([]);
  const [documents, setDocuments] = useState(null);
  const [currentCollection, setCurrentCollection] = useState(null);
  const [loading, setLoading] = useState({
    collections: false,
    documents: false
  });
  const [error, setError] = useState(null);

  const fetchCollections = useCallback(async () => {
    if (!workspaceId) return;
    
    setLoading(prev => ({ ...prev, collections: true }));
    setError(null);
    
    try {
      const response = await authenticatedApi.get(`/workspaces/${workspaceId}/database/collections`);
      setCollections(response);
    } catch (err) {
      console.error('Error fetching collections:', err);
      setError(err.message || 'Failed to fetch collections');
    } finally {
      setLoading(prev => ({ ...prev, collections: false }));
    }
  }, [workspaceId, authenticatedApi]);

  const fetchDocuments = useCallback(async (collectionName, options = {}) => {
    if (!workspaceId || !collectionName) return;
    
    setLoading(prev => ({ ...prev, documents: true }));
    setError(null);
    
    try {
      const params = new URLSearchParams();
      if (options.page) params.append('page', options.page);
      if (options.pageSize) params.append('page_size', options.pageSize);
      if (options.sortField) params.append('sort_field', options.sortField);
      if (options.sortOrder) params.append('sort_order', options.sortOrder);
      if (options.filter) params.append('filter', JSON.stringify(options.filter));
      
      const url = `/workspaces/${workspaceId}/database/collections/${collectionName}/documents${
        params.toString() ? `?${params.toString()}` : ''
      }`;
      
      const response = await authenticatedApi.get(url);
      setDocuments(response);
    } catch (err) {
      console.error('Error fetching documents:', err);
      setError(err.message || 'Failed to fetch documents');
    } finally {
      setLoading(prev => ({ ...prev, documents: false }));
    }
  }, [workspaceId, authenticatedApi]);

  const createDocument = useCallback(async (collectionName, documentData) => {
    if (!workspaceId || !collectionName) return;
    
    try {
      const response = await authenticatedApi.post(
        `/workspaces/${workspaceId}/database/collections/${collectionName}/documents`,
        { data: documentData }
      );
      
      // Refresh documents after creation
      await fetchDocuments(collectionName);
      
      return response;
    } catch (err) {
      console.error('Error creating document:', err);
      throw err;
    }
  }, [workspaceId, authenticatedApi, fetchDocuments]);

  const updateDocument = useCallback(async (collectionName, documentId, updateData) => {
    if (!workspaceId || !collectionName || !documentId) return;
    
    try {
      const response = await authenticatedApi.put(
        `/workspaces/${workspaceId}/database/collections/${collectionName}/documents/${documentId}`,
        { data: updateData }
      );
      
      // Refresh documents after update
      await fetchDocuments(collectionName);
      
      return response;
    } catch (err) {
      console.error('Error updating document:', err);
      throw err;
    }
  }, [workspaceId, authenticatedApi, fetchDocuments]);

  const deleteDocument = useCallback(async (collectionName, documentId) => {
    if (!workspaceId || !collectionName || !documentId) return;
    
    try {
      const response = await authenticatedApi.delete(
        `/workspaces/${workspaceId}/database/collections/${collectionName}/documents/${documentId}`
      );
      
      // Refresh documents after deletion
      await fetchDocuments(collectionName);
      
      return response;
    } catch (err) {
      console.error('Error deleting document:', err);
      throw err;
    }
  }, [workspaceId, authenticatedApi, fetchDocuments]);

  const getCollectionSchema = useCallback(async (collectionName, sampleSize = 100) => {
    if (!workspaceId || !collectionName) return;
    
    try {
      const response = await authenticatedApi.get(
        `/workspaces/${workspaceId}/database/collections/${collectionName}/schema?sample_size=${sampleSize}`
      );
      
      return response;
    } catch (err) {
      console.error('Error fetching collection schema:', err);
      throw err;
    }
  }, [workspaceId, authenticatedApi]);

  return {
    collections,
    documents,
    currentCollection,
    loading,
    error,
    fetchCollections,
    fetchDocuments,
    createDocument,
    updateDocument,
    deleteDocument,
    getCollectionSchema,
    setCurrentCollection,
    setError
  };
};

export default useDatabase; 